"use client"

const SELECTABLE_INPUT_TYPES = new Set([
    "text",
    "search",
    "url",
    "tel",
    "password",
    "",
])
const TEXT_INPUT_TYPES = new Set([...SELECTABLE_INPUT_TYPES, "email", "number"])

export type TextField = HTMLInputElement | HTMLTextAreaElement

export type EditableTarget =
    | {
          kind: "field"
          element: TextField
          start: number
          end: number
          isSelectable: boolean
          isSecret: boolean
      }
    | { kind: "rich"; element: HTMLElement; range: Range | null }

export interface ContextMenuContext {
    selectedText: string
    editable: EditableTarget | null
    scope: HTMLElement | null
}

function isTextField(element: Element): element is TextField {
    if (element instanceof HTMLTextAreaElement) return !element.readOnly
    return (
        element instanceof HTMLInputElement &&
        TEXT_INPUT_TYPES.has(element.type) &&
        !element.readOnly
    )
}

function hasSelectionApi(element: TextField) {
    return (
        element instanceof HTMLTextAreaElement ||
        SELECTABLE_INPUT_TYPES.has(element.type)
    )
}

function fieldSelection(element: TextField) {
    const isSelectable = hasSelectionApi(element)
    const start = isSelectable ? (element.selectionStart ?? 0) : 0
    const end = isSelectable
        ? (element.selectionEnd ?? start)
        : element.value.length
    return { start, end }
}

export function readContextMenuContext(
    target: EventTarget | null
): ContextMenuContext {
    const element = target instanceof Element ? target : null
    const scope =
        element?.closest<HTMLElement>("[data-window-id]") ??
        element?.closest<HTMLElement>("[role=dialog]") ??
        null
    const field = element?.closest("input, textarea")
    if (field && isTextField(field)) {
        const { start, end } = fieldSelection(field)
        const isSecret =
            field instanceof HTMLInputElement && field.type === "password"
        return {
            selectedText: isSecret ? "" : field.value.slice(start, end),
            editable: {
                kind: "field",
                element: field,
                start,
                end,
                isSelectable: hasSelectionApi(field),
                isSecret,
            },
            scope,
        }
    }
    const selection = window.getSelection()
    const range =
        selection && selection.rangeCount > 0
            ? selection.getRangeAt(0).cloneRange()
            : null
    const rich = element?.closest<HTMLElement>("[contenteditable]")
    const isRichEditable = !!rich && rich.isContentEditable
    return {
        selectedText: selection?.toString() ?? "",
        editable:
            rich && isRichEditable
                ? {
                      kind: "rich",
                      element: rich,
                      range:
                          range && rich.contains(range.commonAncestorContainer)
                              ? range
                              : null,
                  }
                : null,
        scope,
    }
}

export function editableText(editable: EditableTarget) {
    if (editable.kind === "rich") return editable.element.textContent ?? ""
    return editable.isSecret ? "" : editable.element.value
}

function restoreSelection(editable: EditableTarget) {
    editable.element.focus()
    if (editable.kind === "field") {
        if (editable.isSelectable) {
            editable.element.setSelectionRange(editable.start, editable.end)
        }
        return
    }
    if (!editable.range) return
    const selection = window.getSelection()
    selection?.removeAllRanges()
    selection?.addRange(editable.range)
}

export async function copyText(text: string) {
    await navigator.clipboard.writeText(text)
}

export async function cutSelection({
    editable,
    selectedText,
}: {
    editable: EditableTarget
    selectedText: string
}) {
    await copyText(selectedText)
    restoreSelection(editable)
    document.execCommand("delete")
}

export async function pasteInto(editable: EditableTarget) {
    const text = await navigator.clipboard.readText()
    restoreSelection(editable)
    document.execCommand("insertText", false, text)
}

export function selectAllIn(context: ContextMenuContext) {
    if (context.editable?.kind === "field") {
        context.editable.element.focus()
        context.editable.element.select()
        return
    }
    const container =
        context.editable?.element ?? context.scope ?? document.body
    if (context.editable) context.editable.element.focus()
    window.getSelection()?.selectAllChildren(container)
}
